"use client";

import { useRef, useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Terminal, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { getTxExplorerUrl } from "@/lib/hoops-contracts";
import { cn } from "@/lib/utils";
import type { LogEntry } from "./types";

interface ExecutionLogProps {
  logs: LogEntry[];
}

const LEVEL_COLORS: Record<LogEntry["level"], string> = {
  info: "bg-blue-500",
  success: "bg-green-500",
  error: "bg-red-500",
};

export function ExecutionLog({ logs }: ExecutionLogProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [collapsed, setCollapsed] = useState(false);

  // Auto-scroll on new entries
  useEffect(() => {
    if (scrollRef.current && !collapsed) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [logs, collapsed]);

  return (
    <Card className="bg-[hsl(var(--background))]">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Terminal className="w-4 h-4 text-muted-foreground" />
            <CardTitle className="text-sm">Execution Log</CardTitle>
            {logs.length > 0 && (
              <Badge variant="secondary" className="text-xs">
                {logs.length}
              </Badge>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 lg:hidden"
            onClick={() => { setCollapsed((c) => !c); }}
          >
            {collapsed ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronUp className="w-4 h-4" />
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className={cn(collapsed && "hidden lg:block")}>
        <div
          ref={scrollRef}
          className="bg-muted/50 rounded-md p-3 font-mono text-xs max-h-64 overflow-y-auto space-y-1.5"
        >
          {logs.length === 0 ? (
            <div className="text-muted-foreground text-center py-4">
              Waiting for actions...
            </div>
          ) : (
            logs.map((entry) => (
              <div key={entry.id} className="flex items-start gap-2">
                <span className="text-muted-foreground whitespace-nowrap shrink-0">
                  {entry.timestamp.toLocaleTimeString("en-US", {
                    hour12: false,
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })}
                </span>
                <span
                  className={cn(
                    "w-1.5 h-1.5 rounded-full mt-1.5 shrink-0",
                    LEVEL_COLORS[entry.level]
                  )}
                />
                <div className="min-w-0">
                  <span
                    className={cn(
                      entry.level === "error" && "text-red-400",
                      entry.level === "success" && "text-green-400"
                    )}
                  >
                    {entry.message}
                  </span>
                  {entry.txHash && (
                    <a
                      href={getTxExplorerUrl(entry.txHash)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-blue-400 hover:underline mt-0.5"
                    >
                      tx: {entry.txHash.slice(0, 12)}...
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
